import { ComponentDataSourceService, DataSourceFunction } from '../../component-data-source.service';
import { LoadDataParams } from '../../../../interfaces/DataSource/DataSourceParams.interface';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { environment } from '../../../../environment/environment';

export const MATERIALS_ENABLED_KEY = 'materials_enabled';

function isOrgSettingTrue(value: unknown): boolean {
  return value === true || value === 'true' || value === 1;
}

/** Default on when key is missing; only off when explicitly false. */
function isMaterialsModuleOn(data: Record<string, unknown> | undefined): boolean {
  if (!data || !Object.prototype.hasOwnProperty.call(data, MATERIALS_ENABLED_KEY)) {
    return true;
  }
  return isOrgSettingTrue(data[MATERIALS_ENABLED_KEY]);
}

function resolveOrgIdFromUser(user: Record<string, unknown> | null | undefined): number | null {
  if (!user) return null;
  const orgId =
    user['organizations_id'] ??
    (user['user_info'] as Record<string, unknown> | undefined)?.['organizations_id'];
  if (orgId == null) return null;
  const n = typeof orgId === 'number' ? orgId : parseInt(String(orgId), 10);
  return Number.isFinite(n) ? n : null;
}

function fetchMaterialsVisible$(
  service: ComponentDataSourceService,
  orgId: number
): Observable<{ visible: boolean }> {
  const settingsObs = service.executeDataSource('org_settings', { organizationId: orgId });
  if (!settingsObs) {
    return of({ visible: false });
  }
  return settingsObs.pipe(
    switchMap((res: { data?: Record<string, unknown> }) => {
      if (!isMaterialsModuleOn(res?.data)) {
        return of({ visible: false });
      }
      return service.http
        .get<{ items?: { deleted?: boolean }[] }>(`${environment.apiUrl}/materials`)
        .pipe(
          map((materialsRes) => {
            const items = (materialsRes.items || []).filter((m) => !m.deleted);
            return { visible: items.length > 0 };
          }),
          catchError(() => of({ visible: false }))
        );
    }),
    catchError(() => of({ visible: false }))
  );
}

/**
 * Event/materiel UI visibility: module on by default, shown only when catalog has materials.
 */
export function duna_materials_enabled(service: ComponentDataSourceService): DataSourceFunction {
  return {
    name: 'duna_materials_enabled',
    description:
      "Whether materials UI should show for the user's org (enabled by default; visible when catalog has items). Returns { visible: boolean }.",
    execute: (_params?: LoadDataParams): Observable<{ visible: boolean }> => {
      const authService = service.authService;
      if (!authService.isAuthenticated) {
        return of({ visible: false });
      }
      const orgIdFromToken = resolveOrgIdFromUser(authService.getCurrentUser());
      if (orgIdFromToken != null) {
        return fetchMaterialsVisible$(service, orgIdFromToken);
      }
      return authService.getUserInfo().pipe(
        take(1),
        switchMap((info) => {
          const orgId = resolveOrgIdFromUser(info as Record<string, unknown>);
          if (orgId == null) {
            return of({ visible: false });
          }
          return fetchMaterialsVisible$(service, orgId);
        }),
        catchError(() => of({ visible: false }))
      );
    },
  };
}
